package ai.wagemix.web.components

import com.varabyte.kobweb.compose.ui.styleModifier
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMCardCornerVar
import ai.wagemix.web.theme.WMCardPaddingCompactVar
import ai.wagemix.web.theme.WMCardPaddingVar
import ai.wagemix.web.theme.WMCardTextGapVar
import ai.wagemix.web.theme.WMCardTitleTextStyle
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMIconTileVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMOverlineTextStyle
import ai.wagemix.web.theme.WMPanelCornerVar
import ai.wagemix.web.theme.WMStatSmallTextStyle
import ai.wagemix.web.theme.WMStatTextStyle
import ai.wagemix.web.theme.WMStatTileGapVar
import ai.wagemix.web.theme.WMTightGapVar
import ai.wagemix.web.theme.wagemix
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import com.varabyte.kobweb.compose.css.TextAlign
import org.jetbrains.compose.web.css.px

/**
 * The redesign's card: surface fill, 1px `outlineVariant` border, 12px corner, the subtle
 * two-layer shadow (`wagemix.shadow.card`), 16px padding on phones and 24px from medium up.
 */
val WMCardStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .backgroundColor(colors.surface)
            .color(colors.onSurface)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.outlineVariant)
            .borderRadius(WMCardCornerVar.value())
            .padding(WMCardPaddingCompactVar.value())
            .gap(WMCardTextGapVar.value())
            .boxShadow(offsetY = 1.px, blurRadius = 3.px, color = colorMode.toPalette().wagemix.shadow)
    }
    FSBreakpoint.Medium { Modifier.padding(WMCardPaddingVar.value()) }
}

/** A larger panel (16px corner): the controls columns of the mix builder, sheets. */
val WMPanelStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .backgroundColor(colors.surface)
            .color(colors.onSurface)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.outlineVariant)
            .borderRadius(WMPanelCornerVar.value())
            .padding(WMCardPaddingCompactVar.value())
            .gap(WMInlineGapVar.value())
    }
    FSBreakpoint.Medium { Modifier.padding(WMCardPaddingVar.value()) }
}

/** The raised shadow (`wagemix.shadow.raised`) for menus and floating sheets. */
val WMRaisedStyle = CssStyle.base {
    Modifier.boxShadow(offsetY = 12.px, blurRadius = 32.px, color = colorMode.toPalette().wagemix.shadow)
}

/**
 * A card with an optional header row ([title] plus a trailing [action] slot) above [content].
 */
@Composable
fun WMCard(
    modifier: Modifier = Modifier,
    title: String? = null,
    subtitle: String? = null,
    action: (@Composable RowScope.() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    Column(modifier = WMCardStyle.toModifier().then(modifier)) {
        if (title != null || action != null) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())) {
                    title?.let { SpanText(it, WMCardTitleTextStyle.toModifier()) }
                    subtitle?.let { SpanText(it, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant)) }
                }
                action?.invoke(this)
            }
        }
        content()
    }
}

/** A panel: like [WMCard] with the 16px corner and no shadow. */
@Composable
fun WMPanel(
    modifier: Modifier = Modifier,
    title: String? = null,
    action: (@Composable RowScope.() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(modifier = WMPanelStyle.toModifier().then(modifier)) {
        if (title != null || action != null) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()),
                verticalAlignment = Alignment.CenterVertically
            ) {
                title?.let { SpanText(it, WMCardTitleTextStyle.toModifier().fillMaxWidth()) }
                action?.invoke(this)
            }
        }
        content()
    }
}

/** Penpot `Wagemix / Stat Tile`, property `Size`. */
enum class WMStatTileSize { Regular, Compact }

/**
 * Penpot `Wagemix / Stat Tile`: an overline [label], the [value] in the stat type and a
 * [caption] line ("2025 · per year · +3.2% vs 2024"). [projected] tints the value teal (`Kind: Projected`);
 * [highlight] fills the tile with the primary container (`Kind: Highlight`, the median; design deviation H3), and its
 * text takes the container's colour, projected or not.
 */
@Composable
fun WMStatTile(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    caption: String? = null,
    size: WMStatTileSize = WMStatTileSize.Regular,
    projected: Boolean = false,
    highlight: Boolean = false
) {
    val colors = ColorMode.current.toPalette().fs
    val valueStyle = when (size) {
        WMStatTileSize.Regular -> WMStatTextStyle
        WMStatTileSize.Compact -> WMStatSmallTextStyle
    }
    val muted = if (highlight) colors.onPrimaryContainer else colors.onSurfaceVariant
    val valueColor = when {
        highlight -> colors.onPrimaryContainer
        projected -> colors.secondary
        else -> colors.onSurface
    }
    // Inline, so the fill wins over the card style's whatever order the two style sheets load in.
    val fill = if (highlight) {
        Modifier.backgroundColor(colors.primaryContainer)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.primaryContainer)
            .styleModifier { property("box-shadow", "none") }
    } else {
        Modifier
    }
    Column(modifier = WMCardStyle.toModifier().then(fill).gap(WMStatTileGapVar.value()).then(modifier)) {
        SpanText(label, WMOverlineTextStyle.toModifier().color(muted))
        SpanText(value, valueStyle.toModifier().color(valueColor))
        caption?.let { SpanText(it, WMCaptionTextStyle.toModifier().color(muted)) }
    }
}


/** A 40px rounded tile holding one glyph (feature cards, empty states, notices). */
@Composable
fun WMIconTile(glyph: WMIconGlyph, modifier: Modifier = Modifier, accent: Boolean = true) {
    val colors = ColorMode.current.toPalette().fs
    Box(
        modifier = Modifier
            .size(WMIconTileVar.value())
            .borderRadius(WMCardCornerVar.value())
            .backgroundColor(if (accent) colors.primaryContainer else colors.surfaceContainer)
            .color(if (accent) colors.onPrimaryContainer else colors.onSurfaceVariant)
            .flexShrink(0)
            .then(modifier),
        contentAlignment = Alignment.Center
    ) {
        WMIcon(glyph, accessibilityString = null)
    }
}

/**
 * Penpot `Wagemix / Empty State`: an icon tile, a title, a message and an optional action.
 */
@Composable
fun WMEmptyState(
    glyph: WMIconGlyph,
    title: String,
    message: String,
    modifier: Modifier = Modifier,
    action: (@Composable () -> Unit)? = null
) {
    val colors = ColorMode.current.toPalette().fs
    Column(
        modifier = WMCardStyle.toModifier().gap(WMInlineGapVar.value()).textAlign(TextAlign.Center).then(modifier),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        WMIconTile(glyph, accent = false)
        SpanText(title, WMCardTitleTextStyle.toModifier())
        SpanText(message, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        action?.invoke()
    }
}
